/**
 * ChangelogContext — Singleton changelog state for the entire app.
 *
 * POPUP LOGIC (why it wasn't showing on live site):
 * ─────────────────────────────────────────────────
 * The old code stored the BUILD_ID in localStorage and compared on
 * every load. On a live site, localStorage persists across sessions,
 * so returning visitors (and even first-time visitors on shared
 * browsers) never saw the popup again after dismissing once.
 *
 * NEW APPROACH — two-tier check:
 *   1. sessionStorage key  → shows toast+modal ONCE per browser session
 *      (clears when tab/window closes). Every new visit = fresh session.
 *   2. localStorage key    → tracks "permanently dismissed" per build.
 *      Only written when the user explicitly clicks "Got it" or "Dismiss".
 *
 * This means:
 *   - Brand-new visitors: always see the popup ✅
 *   - Returning visitors who dismissed: don't see it again ✅
 *   - Returning visitors who closed tab without dismissing: see it again ✅
 *   - New deploy (new BUILD_ID): everyone sees it again ✅
 */
import React, {
  createContext, useContext, useState,
  useEffect, useCallback, useRef,
} from 'react';
import {
  APP_VERSION, BUILD_ID,
  getLatestEntry, getRandomToast,
} from '../constants/changelog';
import supabase from '../supabase/supabase';

const ChangelogContext = createContext(null);

// localStorage  — "user permanently dismissed this build"
const LS_DISMISSED_KEY = 'vd_dismissed_build';
// sessionStorage — "user has already been shown popup this session"
const SS_SHOWN_KEY     = 'vd_shown_session';
const BC_CHANNEL_NAME  = 'vd_changelog_sync';

export const ChangelogProvider = ({ children }) => {
  const [showModal,      setShowModal]      = useState(false);
  const [showToast,      setShowToast]      = useState(false);
  const [showBanner,     setShowBanner]     = useState(false);
  const [hasNewUpdate,   setHasNewUpdate]   = useState(false);
  const [entry,          setEntry]          = useState(null);
  const [toastMessage,   setToastMessage]   = useState('');
  const [liveDeployment, setLiveDeployment] = useState(null);

  const bcRef    = useRef(null);
  const timerRef = useRef(null);

  // ── Storage helpers ──────────────────────────────────────

  /** Returns the build ID the user last permanently dismissed. */
  const getDismissedBuildId = () => {
    try { return localStorage.getItem(LS_DISMISSED_KEY); } catch { return null; }
  };

  /** Returns true if we already showed the popup this browser session. */
  const wasShownThisSession = () => {
    try { return sessionStorage.getItem(SS_SHOWN_KEY) === BUILD_ID; } catch { return false; }
  };

  /** Mark as shown for this session (resets when tab closes). */
  const markShownThisSession = () => {
    try { sessionStorage.setItem(SS_SHOWN_KEY, BUILD_ID); } catch { /* private */ }
  };

  /** Permanently dismiss — user clicked "Got it". */
  const persistDismissed = (buildId) => {
    try {
      localStorage.setItem(LS_DISMISSED_KEY, buildId);
      sessionStorage.setItem(SS_SHOWN_KEY, buildId);
    } catch { /* private */ }
  };

  /**
   * shouldShow(buildId)
   * Returns true when the popup should be displayed:
   *   - User has NOT permanently dismissed this exact build, AND
   *   - We have NOT already shown it this session.
   */
  const shouldShow = (buildId) => {
    const dismissed = getDismissedBuildId();
    if (dismissed === buildId) return false;   // user said "got it"
    if (wasShownThisSession())  return false;  // already shown this tab session
    return true;
  };

  // ── Clear all UI ─────────────────────────────────────────
  const clearAllUI = useCallback(() => {
    setShowModal(false);
    setShowToast(false);
    setShowBanner(false);
    setHasNewUpdate(false);
    clearTimeout(timerRef.current);
  }, []);

  // ── Trigger notification by severity ────────────────────
  const triggerNotification = useCallback((deployment, severity, localEntry) => {
    const toast = getRandomToast(localEntry || deployment);
    setEntry(localEntry || null);
    setToastMessage(toast);
    setHasNewUpdate(true);
    setLiveDeployment(deployment);
    markShownThisSession();

    if (severity === 'low') return; // badge only — no toast/modal

    if (severity === 'high') {
      setShowBanner(true);
      return;
    }

    // medium — slide-in toast, then modal after 8s
    setShowToast(true);
    timerRef.current = setTimeout(() => {
      setShowToast(false);
      timerRef.current = setTimeout(() => {
        if (localEntry) setShowModal(true);
      }, 500);
    }, 8000);
  }, []);

  // ── Evaluate a deployment row ────────────────────────────
  const evaluateDeployment = useCallback((deployment, localEntry) => {
    if (!deployment || deployment.silent) return;
    if (!shouldShow(deployment.build_id))  return;
    triggerNotification(deployment, deployment.severity, localEntry);
  }, [triggerNotification]);

  // ── BroadcastChannel — cross-tab sync ───────────────────
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    try {
      bcRef.current = new BroadcastChannel(BC_CHANNEL_NAME);
      bcRef.current.onmessage = (event) => {
        if (event.data?.type === 'DISMISS') clearAllUI();
        if (event.data?.type === 'NEW_DEPLOY') {
          const { deployment, severity } = event.data;
          triggerNotification(deployment, severity, getLatestEntry());
        }
      };
    } catch { /* unavailable */ }
    return () => { try { bcRef.current?.close(); } catch { /* silent */ } };
  }, [clearAllUI, triggerNotification]);

  // ── Initial load + Realtime subscription ────────────────
  useEffect(() => {
    let channel = null;

    const init = async () => {
      const localEntry = getLatestEntry();

      // ── Try Supabase RPC first ───────────────────────────
      let supabaseOk = false;
      try {
        const { data, error } = await supabase.rpc('get_latest_deployment');
        if (!error) {
          supabaseOk = true;
          const latest = Array.isArray(data) ? data[0] : data;
          if (latest) {
            setTimeout(() => evaluateDeployment(latest, localEntry), 2500);
          } else {
            // RPC returned nothing — fall back to local check
            if (localEntry && shouldShow(BUILD_ID)) {
              setTimeout(() => triggerNotification(
                { build_id: BUILD_ID, severity: 'medium', silent: false },
                'medium', localEntry
              ), 2500);
            }
          }
        }
      } catch { /* network error — fall through */ }

      // ── Supabase unavailable — use local BUILD_ID ────────
      if (!supabaseOk) {
        if (localEntry && shouldShow(BUILD_ID)) {
          setTimeout(() => triggerNotification(
            { build_id: BUILD_ID, severity: 'medium', silent: false },
            'medium', localEntry
          ), 2500);
        }
      }

      // ── Realtime — unique channel name (Strict Mode safe) ─
      const channelName = `deployments-${Math.random().toString(36).substring(2, 9)}`;
      channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'd2f_deployments' },
          (payload) => {
            const dep = payload.new;
            if (!dep || dep.silent) return;
            if (!shouldShow(dep.build_id)) return;
            const le = getLatestEntry();
            triggerNotification(dep, dep.severity, le);
            try {
              bcRef.current?.postMessage({
                type: 'NEW_DEPLOY', deployment: dep, severity: dep.severity,
              });
            } catch { /* silent */ }
          }
        )
        .subscribe();
    };

    init();

    return () => {
      clearTimeout(timerRef.current);
      if (channel) supabase.removeChannel(channel);
    };
  }, []); // run once on mount — evaluateDeployment/triggerNotification are stable refs

  // ── Public dismiss — user clicked "Got it" / "Dismiss" ──
  const dismiss = useCallback(() => {
    const buildId = liveDeployment?.build_id || BUILD_ID;
    persistDismissed(buildId);
    clearAllUI();
    try { bcRef.current?.postMessage({ type: 'DISMISS' }); } catch { /* silent */ }
  }, [liveDeployment, clearAllUI]);

  const dismissToast = useCallback(() => {
    clearTimeout(timerRef.current);
    setShowToast(false);
    // Don't persist — user just closed the toast, modal can still show
  }, []);

  const dismissBanner = useCallback((andRefresh = false) => {
    dismiss();
    if (andRefresh) window.location.reload();
  }, [dismiss]);

  // ── openChangelog — Navbar badge click ──────────────────
  const openChangelog = useCallback(() => {
    const localEntry = getLatestEntry();
    if (localEntry) {
      setEntry(localEntry);
      setShowModal(true);
    }
  }, []);

  const expandToModal = useCallback(() => {
    clearTimeout(timerRef.current);
    setShowToast(false);
    setTimeout(() => setShowModal(true), 200);
  }, []);

  const value = {
    showModal, showToast, showBanner,
    hasNewUpdate, entry, toastMessage, liveDeployment,
    dismiss, dismissToast, dismissBanner,
    openChangelog, expandToModal,
  };

  return (
    <ChangelogContext.Provider value={value}>
      {children}
    </ChangelogContext.Provider>
  );
};

export const useChangelog = () => {
  const ctx = useContext(ChangelogContext);
  if (!ctx) throw new Error('useChangelog must be used inside <ChangelogProvider>');
  return ctx;
};